import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";
import { Text } from "../typography/text";
import { Title } from "../typography/title";

type AlertTone = Extract<
  Tone,
  "neutral" | "positive" | "warning" | "negative" | "informative"
>;

const alertVariants = cva(
  [
    "w-full",
    "flex",
    "items-start",
    "justify-between",
    "gap-(--spacing-24)",
    "p-(--spacing-12)",
    "rounded-(--radius-sm)",
  ],
  {
    variants: {
      tone: {
        neutral: ["bg-surface-container-low", "text-surface-on-surface"],
        positive: ["bg-positive-container", "text-positive-on-container"],
        warning: [
          "bg-accessibility-warning-container",
          "text-warning-on-container",
        ],
        negative: ["bg-negative-container", "text-negative-on-container"],
        informative: [
          "bg-informative-container",
          "text-informative-on-container",
        ],
      },
    },
    defaultVariants: {
      tone: "neutral",
    },
  },
);

const iconMap: Record<AlertTone, IconName> = {
  neutral: "circle-info",
  informative: "circle-info",
  positive: "circle-check",
  warning: "warning",
  negative: "warning",
};

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "title"> {
  /** Tone controls icon and background color. Defaults to "neutral". */
  tone?: AlertTone;
  /** Primary alert message. */
  title: React.ReactNode;
  /** Optional supporting line shown beneath the title. */
  subtext?: React.ReactNode;
  /** Optional action controls, typically one or two buttons. */
  action?: React.ReactNode;
  /**
   * Called when the dismiss button is clicked. The dismiss button only renders
   * when this is provided — the consumer controls Alert visibility.
   */
  onDismiss?: () => void;
}

/**
 * Inline contextual notification with a semantic icon and message.
 * Use for field-level or section-level feedback.
 *
 * @example
 * <Alert tone="positive" title="Password updated successfully." />
 * <Alert tone="negative" title="Please fix the errors below before continuing." />
 *
 * @example
 * <Alert
 *   title="After saving, this facility will move to the new customer."
 *   subtext="So make sure to save your stuff"
 *   action={
 *     <>
 *       <Button variant="ghost" size="sm">Learn more</Button>
 *       <Button variant="outline" size="sm" icon="arrow-up-right">
 *         View facility
 *       </Button>
 *     </>
 *   }
 *   onDismiss={() => setOpen(false)}
 * />
 */
function Alert({
  tone = "neutral",
  title,
  subtext,
  action,
  onDismiss,
  ...props
}: AlertProps) {
  const isNegative = tone === "negative";
  const hasActions = Boolean(action || onDismiss);

  return (
    <div
      role={isNegative ? "alert" : "note"}
      aria-live={isNegative ? "assertive" : undefined}
      className={cn(alertVariants({ tone }))}
      {...props}
    >
      <div className="flex flex-1 items-start gap-(--spacing-8) min-w-0">
        <Icon name={iconMap[tone]} size="md" tone="inherit" />
        <div className="flex flex-col gap-(--spacing-8) min-w-0">
          <Title as="span" size="sm" style={{ color: "inherit" }}>
            {title}
          </Title>
          {subtext ? (
            <Text as="span" size="sm" style={{ color: "inherit" }}>
              {subtext}
            </Text>
          ) : null}
        </div>
      </div>
      {hasActions ? (
        <div className="flex shrink-0 items-start gap-(--spacing-8)">
          {action}
          {onDismiss ? (
            <IconButton
              icon="x-close"
              aria-label="Dismiss alert"
              variant="ghost"
              size="sm"
              onClick={onDismiss}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export { Alert };
